import { useCallback, useEffect, useRef } from 'react';

import { ACTIVITY_EVENTS, CHECK_INTERVAL_MS, THROTTLE_MS } from './styles';
import { IdleTimeoutCheckOptions, UseIdleTimeoutOptions } from './types';

const checkIdleTimeout = ({
  elapsed,
  timeoutMs,
  warningMs,
  warningFiredRef,
  onWarningRef,
  onTimeoutRef,
}: IdleTimeoutCheckOptions): void => {
  if (elapsed >= timeoutMs) {
    onTimeoutRef.current();
    return;
  }

  const warningThreshold = Math.max(0, timeoutMs - warningMs);
  if (elapsed >= warningThreshold && !warningFiredRef.current) {
    warningFiredRef.current = true;
    onWarningRef.current();
  }
};

export const useIdleTimeout = ({
  timeoutMs,
  warningMs,
  onWarning,
  onTimeout,
  enabled,
}: UseIdleTimeoutOptions): { resetIdleTimer: () => void } => {
  const lastActivityRef = useRef(Date.now());
  const warningFiredRef = useRef(false);
  const lastThrottleRef = useRef(0);
  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);

  onWarningRef.current = onWarning;
  onTimeoutRef.current = onTimeout;

  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningFiredRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastThrottleRef.current < THROTTLE_MS) return;
      lastThrottleRef.current = now;
      lastActivityRef.current = now;
      warningFiredRef.current = false;
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      checkIdleTimeout({ elapsed, timeoutMs, warningMs, warningFiredRef, onWarningRef, onTimeoutRef });
    }, CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastActivityRef.current;
      checkIdleTimeout({ elapsed, timeoutMs, warningMs, warningFiredRef, onWarningRef, onTimeoutRef });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [enabled, timeoutMs, warningMs]);

  return { resetIdleTimer };
};
